import * as React from "react";

import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Chip from "@mui/material/Chip";

import useBuildVersionCheck from "hooks/use-build-version-check.hook";
import { getAppBuildNumber } from "utils/lazy-with-retry.util";

import { meetsStaleBuildThreshold } from "./stale-build-pill-policy.util";
import useStaleBuildReload from "./use-stale-build-reload.hook";

/**
 * ENG-747 Phase A, Tier 1: a small persistent pill, not a snackbar/toast/modal (that's
 * the deferred Tier 2). Behind `STALE_BUILD_DETECTION_FEATURE` via `useBuildVersionCheck`,
 * which no-ops entirely when the flag is off — this component still mounts (it lives in
 * the always-rendered top bar) but renders nothing and does no work.
 *
 * Visible only once a detected newer build crosses `meetsStaleBuildThreshold` — existing
 * is not enough, see that util's doc. Click reloads (verified — see
 * `use-stale-build-reload.hook.ts`); the X dismisses this specific build only, per
 * `useBuildVersionCheck`'s per-sha dismiss.
 */
export default function StaleBuildPill() {
  const {
    isStale,
    latestSha,
    builtAt,
    latestBuildNumber,
    minSupportedBuildNumber,
    dismiss
  } = useBuildVersionCheck();
  const { handleReloadClick } = useStaleBuildReload({ latestSha, dismiss });

  const shouldShow =
    isStale &&
    meetsStaleBuildThreshold({
      builtAt,
      latestBuildNumber,
      minSupportedBuildNumber,
      currentBuildNumber: getAppBuildNumber(),
      now: Date.now()
    });

  if (!shouldShow) return null;

  return (
    <Chip
      icon={<RefreshIcon fontSize="small" />}
      label="Update available"
      onClick={handleReloadClick}
      onDelete={dismiss}
      deleteIcon={<CloseIcon fontSize="small" />}
      size="small"
      data-testid="stale-build-pill"
      aria-label="A newer version of Suno is available — click to refresh"
      // Palette set via sx, not the `color` prop — MuiChip's theme styleOverrides
      // flattens every filled chip's `color` to the same lilac (see the Chip footgun
      // note in CLAUDE.md); sx wins over styleOverrides so this is the only way a
      // filled chip's colour actually carries meaning.
      sx={{
        fontWeight: 600,
        backgroundColor: "warning.main",
        color: "warning.contrastText",
        "& .MuiChip-icon, & .MuiChip-deleteIcon": {
          color: "warning.contrastText"
        },
        "&:hover": {
          backgroundColor: "warning.dark"
        }
      }}
    />
  );
}
